import type React from 'react';

import { cn } from 'utils/cn';
import type { TagProps } from './types';
import { TagVariant } from './types';

const VARIANT_CLASSES: Record<TagVariant, { bg: string; text: string }> = {
  [TagVariant.WHITE]: { bg: 'bg-background', text: 'text-primary' },
  [TagVariant.LIGHT_GREY]: { bg: 'bg-background-page', text: 'text-primary' },
  [TagVariant.DARK_GREY]: {
    bg: 'bg-[var(--tw-grey-70)]',
    text: 'text-[var(--tw-grey-40)]',
  },
  [TagVariant.GREY]: {
    bg: 'bg-[var(--tw-grey-50)]',
    text: 'text-primary-inverse',
  },
  [TagVariant.BLACK]: {
    bg: 'bg-[var(--tw-purple-grey-100)]',
    text: 'text-primary-inverse',
  },
  [TagVariant.YELLOW]: { bg: 'bg-background-warning', text: 'text-primary' },
  [TagVariant.RED]: {
    bg: 'bg-background-danger',
    text: 'text-primary-inverse',
  },
  [TagVariant.GREEN]: {
    bg: 'bg-background-success',
    text: 'text-primary-inverse',
  },
  [TagVariant.BLUE]: {
    bg: 'bg-[var(--tw-blue-90)]',
    text: 'text-primary-inverse',
  },
  [TagVariant.LIGHT_PURPLE]: {
    bg: 'bg-background-accent',
    text: 'text-accent',
  },
  [TagVariant.PURPLE]: {
    bg: 'bg-accent',
    text: 'text-primary-inverse',
  },
  [TagVariant.GENAI]: {
    bg: 'bg-[var(--tw-gen-ai-tag-background)]',
    text: 'text-primary',
  },
  [TagVariant.TRANSPARENT]: { bg: 'bg-transparent', text: 'text-helper' },
};

const Tag: React.FC<TagProps> = ({
  className,
  variant = TagVariant.WHITE,
  size = 'regular',
  LeftIcon,
  RightIcon,
  Icon,
  children,
  textDataTestId = 'tag-text',
  textClassName,
  ...props
}) => {
  const isIconOnly = Icon !== undefined || children === undefined;
  const isStringChildren = typeof children === 'string';
  const { bg, text } = VARIANT_CLASSES[variant];

  return (
    <span
      className={cn(
        'inline-flex gap-xs1 items-center justify-center min-w-[24px] rounded-xSmall',
        size === 'small' ? 'h-sm2' : 'h-sm3',
        isIconOnly
          ? size === 'regular'
            ? 'p-xs1'
            : 'p-xxs2'
          : size === 'regular'
            ? 'px-xs2 py-xs1'
            : 'px-xs2 py-xxs2',
        bg,
        text,
        className,
      )}
      {...props}
    >
      {Icon ? (
        <Icon size="sm" />
      ) : isStringChildren ? (
        <>
          {LeftIcon && <LeftIcon size="sm" />}
          <span
            className={cn(
              'font-body font-semibold leading-tight text-body-small text-inherit',
              textClassName,
            )}
            data-test-id={textDataTestId}
          >
            {children}
          </span>
          {RightIcon && <RightIcon size="sm" />}
        </>
      ) : (
        <>
          {LeftIcon && <LeftIcon size="sm" />}
          {children}
          {RightIcon && <RightIcon size="sm" />}
        </>
      )}
    </span>
  );
};

export { Tag };

export { type TagProps, type TagSize, TagVariant } from './types';
